import { AuthModel, ParticipantModel } from '@blazeo.com/calendar-client';
import { BaseFrontdoorAdapter } from "../frontdoor/BaseFrontdoorAdapter";
import config from "@/config";

/**
 * SmartHubCalendarAuthAdapter
 *
 * Wraps @blazeo.com/calendar-client AuthModel for the calendar OAuth
 * authorization lifecycle.
 */
export class SmartHubCalendarAuthAdapter extends BaseFrontdoorAdapter {
  constructor(options) {
    super(options);
    // Explicitly set the base URL for the fallback REST calls
    this.baseUrl = config.blazeoApiUrl;
  }

  /**
   * Fetch available calendar providers (Google, Microsoft, etc.)
   * @returns {Promise<{ providers: Array<{ id: string, name: string, icon: string, type: string }> }>}
   */
  async getProviders() {
    // FALLBACK REST IMPLEMENTATION (Use this if AuthModel is missing)
    /*
    const response = await this.get("/Auth/CalendarProviders", {});
    return {
      providers: (response.providers || []).map(this._normalizeProvider),
    };
    */
    const response = await AuthModel.getCalendarProviders();
    const items = response.data || response.providers || [];
    return {
      providers: items.map(p => this._normalizeProvider(p)),
    };
  }

  /**
   * Get the OAuth authorization URL for a given provider + participantId.
   * @param {{ provider: string, participantId: string, redirectUri?: string }} params
   * @returns {Promise<{ authorizationUrl: string }>}
   */
  async getAuthorizationUrl({ provider, participantId, redirectUrl, authorizedByParticipantId }) {
    // FALLBACK REST IMPLEMENTATION (Use this if AuthModel is missing)
    /*
    const response = await this.get("/Auth/AuthorizationUrl", {
      provider,
      participant_id: participantId,
      redirectUrl,
    });
    return { authorizationUrl: response.authorizationUrl };
    */
    const response = await AuthModel.getAuthorizationUrl(
      participantId,
      provider,
      { redirectUrl, authorizedByParticipantId },
    );
    const data = response.data || response;
    return { authorizationUrl: data.authorizationUrl || data.AuthorizationUrl };
  }

  /**
   * Self-serve connect for a logged-in user with no participant yet.
   * Backend creates the participant from the provider account after OAuth.
   * @param {{ companyKey: string, provider: string }} params
   * @returns {Promise<{ authorizationUrl: string, requestId: string, data: object }>}
   */
  async startDirectAuthorization({ companyKey, provider }) {
    const providers = await AuthModel.getCalendarProviders();
    const response = await AuthModel.startDirectAuthorization(companyKey, provider);
    const data = response.data || response;
    return {
      ...response,
      data,
      requestId: data.requestId || data.RequestId,
      authorizationUrl: data.authorizationUrl || data.AuthorizationUrl,
    };
  }

  /**
   * Authorization status after OAuth (direct-connect verify step).
   * @param {string} participantId
   */
  async getAuthorizationStatus(participantId) {
    const response = await AuthModel.getAuthorizationStatus(participantId);
    return response?.data || response;
  }

  openOAuthPopup(authorizationUrl, opts) {
    return AuthModel.openOAuthPopup(authorizationUrl, opts);
  }

  onCalendarAuthMessage(handler, popup) {
    return AuthModel.onCalendarAuthMessage(handler, popup);
  }

  /**
   * Check whether a calendar account is connected.
   * @param {{ participantId: string, provider: string }} params
   * @returns {Promise<{ connected: boolean, participantId: string, provider: string, connectedAt: string|null }>}
   */
  async getStatus(participantId) {
    try {
      const response = await AuthModel.IsAuthorized(participantId);
      const data = this._normalizeStatus(response?.data || {});
      const authErr = data.authorizationError || data.AuthorizationError;
      if (typeof authErr === "string") {
        const lowerErr = authErr.toLowerCase();
        if (lowerErr.includes("match") && lowerErr.includes("same email")) {
          const replacement = "Email Mismatch: Submitted email differs from the approved one. Please approve the submitted email address.";
          if (data.authorizationError != null) data.authorizationError = replacement;
          if (data.AuthorizationError != null) data.AuthorizationError = replacement;
        }
      }
      return data;
    } catch (error) {
      const errMsg = error?.message || String(error);
      const lowerMsg = errMsg.toLowerCase();
      if (lowerMsg.includes("match") && lowerMsg.includes("same email")) {
        throw new Error("Email Mismatch: Submitted email differs from the approved one. Please approve the submitted email address.");
      }
      throw error;
    }
  }

  /**
   * Adds a new participant if they do not already exist.
   * @param {{ name: string, email: string, companyKey: string }} params
   * @returns {Promise<{ participantId: string } | null>}
   */
  async addParticipant({ name, email, companyKey, authorizedByParticipantId }) {
    const payload = { name, email, companyKey, alias: name, authorizedByParticipantId };
    // FALLBACK REST IMPLEMENTATION (Use this if ParticipantModel is missing)
    /*
    const response = await this.post("/Participant/Add", payload);
    return { participantId: response.data?.ParticipantId || response.ParticipantId };
    */
    const participant = await ParticipantModel.add(payload);
    if (!participant) {
      return null;
    }
    const participantId = participant.participantId || participant.ParticipantId;
    return { participantId: participantId ? String(participantId) : null };
  }

  /**
   * Fetches a participant by email using the calendar-client.
   * @param {{ email: string, companyKey: string }} params
   * @returns {Promise<{ participantId: string } | null>}
   */
  async getParticipantByEmail({ email, companyKey }) {
    if (!companyKey) throw new Error("companyKey is required to fetch participants");
    const existing = await ParticipantModel.getByEmail(email, companyKey);
    if (existing) {
      const participantId = existing.participantId || existing.ParticipantId;
      return {
        participantId: participantId ? String(participantId) : null,
      };
    }
    return null;
  }

  // ─── NORMALIZERS ───────────────────────────────────────────

  _normalizeProvider(raw) {
    return {
      id: raw.id || raw.emailProvider?.toString() || raw.EmailProvider?.toString(),
      name: raw.name || raw.Name,
      icon: raw.icon || raw.displayName || raw.DisplayName,
      type: raw.type || raw.key || raw.Key,
      redirectUrl: raw.redirectUrl || raw.RedirectUrl
    };
  }

  _normalizeStatus(raw) {
    const authorizedBy =
      raw.authorizedByParticipantId ??
      raw.AuthorizedByParticipantId ??
      null;
    const authorizationSource =
      raw.authorizationSource ??
      raw.AuthorizationSource ??
      null;
    const authorizedOn =
      raw.authorizedOn ??
      raw.AuthorizedOn ??
      null;
    const isApproved =
      raw.isApproved ??
      raw.IsApproved ??
      raw.isAuthorized ??
      raw.IsAuthorized ??
      false;

    return {
      ...raw,
      status: raw.connected || isApproved || raw.isAuthorized ? 1 : 0,
      participantId: raw.participantId || raw.ParticipantId || '',
      memberId: raw.participantId || raw.ParticipantId || raw.id || '',
      id: raw.participantId || raw.ParticipantId || raw.id,
      provider: raw.provider || raw.ProviderKey || raw.emailProvider || raw.EmailProvider || '',
      isApproved,
      name: raw.alias || raw.Alias || raw.name || raw.Name,
      authorizationError: raw.authorizationError ?? raw.AuthorizationError ?? '',
      authorizedByParticipantId: authorizedBy,
      AuthorizedByParticipantId: authorizedBy,
      authorizationSource,
      AuthorizationSource: authorizationSource,
      authorizedOn,
      AuthorizedOn: authorizedOn,
    };
  }
}
